/**
 * The wall of code behind a sign-in page.
 *
 * Drawn in the document rather than loaded as a picture: it costs no request, stays
 * sharp on any screen, and is the one background that is *about* what this product is
 * for. The lines are real — the opening of the page every student starts from — so a
 * child who has done one lesson recognises them.
 *
 * It is scenery and nothing else: hidden from screen readers, unselectable, and faded
 * out through the middle so the window that carries the form always sits on quiet
 * ground. Nothing here is ever read; it is never the place a password is typed.
 */

import { cn } from '@praxadon/ui';

/** A line of the wall: pieces of text, each with the colour its kind of token gets. */
type Token = 'tag' | 'attr' | 'string' | 'comment' | 'keyword' | 'fn';
type Piece = [text: string, token?: Token | undefined];

const LINES: Piece[][] = [
  [['<!doctype html>', 'tag']],
  [
    ['<html ', 'tag'],
    ['lang', 'attr'],
    ['="', 'tag'],
    ['en', 'string'],
    ['">', 'tag'],
  ],
  [['<head>', 'tag']],
  [
    ['  <meta ', 'tag'],
    ['charset', 'attr'],
    ['="', 'tag'],
    ['utf-8', 'string'],
    ['" />', 'tag'],
  ],
  [
    ['  <title>', 'tag'],
    ['My first website', 'string'],
    ['</title>', 'tag'],
  ],
  [
    ['  <link ', 'tag'],
    ['rel', 'attr'],
    ['="', 'tag'],
    ['stylesheet', 'string'],
    ['" ', 'tag'],
    ['href', 'attr'],
    ['="', 'tag'],
    ['style.css', 'string'],
    ['" />', 'tag'],
  ],
  [['</head>', 'tag']],
  [['<body>', 'tag']],
  [
    ['  <h1>', 'tag'],
    ['Hello, world!', 'string'],
    ['</h1>', 'tag'],
  ],
  [
    ['  <p>', 'tag'],
    ['I made this page myself.', 'string'],
    ['</p>', 'tag'],
  ],
  [
    ['  <button ', 'tag'],
    ['id', 'attr'],
    ['="', 'tag'],
    ['go', 'string'],
    ['">', 'tag'],
    ['Say hello', 'string'],
    ['</button>', 'tag'],
  ],
  [
    ['  <script ', 'tag'],
    ['src', 'attr'],
    ['="', 'tag'],
    ['app.js', 'string'],
    ['"></script>', 'tag'],
  ],
  [['</body>', 'tag']],
  [['</html>', 'tag']],
  [['', undefined]],
  [['/* style.css */', 'comment']],
  [['body', 'fn'], [' {']],
  [['  font-family', 'attr'], [': system-ui, sans-serif;']],
  [['  background', 'attr'], [': ', undefined], ['#0f172a', 'string'], [';']],
  [['  color', 'attr'], [': ', undefined], ['#e2e8f0', 'string'], [';']],
  [['}']],
  [['h1', 'fn'], [' {']],
  [['  font-size', 'attr'], [': ', undefined], ['2.5rem', 'string'], [';']],
  [['  letter-spacing', 'attr'], [': ', undefined], ['-0.02em', 'string'], [';']],
  [['}']],
  [['', undefined]],
  [['// app.js', 'comment']],
  [
    ['const', 'keyword'],
    [' button = '],
    ['document', 'fn'],
    ['.querySelector('],
    ["'#go'", 'string'],
    [');'],
  ],
  [
    ['button.', undefined],
    ['addEventListener', 'fn'],
    ['('],
    ["'click'", 'string'],
    [', () '],
    ['=>', 'keyword'],
    [' {'],
  ],
  [
    ['  ', undefined],
    ['const', 'keyword'],
    [' name = '],
    ['prompt', 'fn'],
    ['('],
    ["'What is your name?'", 'string'],
    [');'],
  ],
  [['  ', undefined], ['if', 'keyword'], [' (name) {']],
  [['    document.body.', undefined], ['append', 'fn'], ['(`Hello, ${name}!`);']],
  [['  }']],
  [['});']],
  [['', undefined]],
  [['// Every page you write starts here.', 'comment']],
];

const TOKEN: Record<Token, string> = {
  tag: 'text-sky-300',
  attr: 'text-violet-300',
  string: 'text-emerald-300',
  comment: 'text-slate-500 italic',
  keyword: 'text-fuchsia-300',
  fn: 'text-amber-200',
};

function Column({ className }: { className?: string }) {
  return (
    <pre
      className={cn(
        'm-0 font-mono text-[13px] leading-[1.75] whitespace-pre text-slate-400',
        className,
      )}
    >
      {LINES.map((line, index) => (
        // The wall never changes, so its own position is a perfectly good key.
        <div key={index}>
          {line.map(([text, token], piece) => (
            <span key={piece} className={token ? TOKEN[token] : undefined}>
              {text}
            </span>
          ))}
          {line.length === 1 && line[0]?.[0] === '' ? ' ' : null}
        </div>
      ))}
    </pre>
  );
}

/**
 * Two columns on a wide screen, one on a phone, faded away through the middle so the
 * window can sit on it. The fade is a mask rather than a scrim: the code stays code,
 * it simply stops competing.
 */
export function CodeBackdrop() {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 -z-10 overflow-hidden select-none"
      style={{ backgroundColor: '#080a12' }}
    >
      {/* A wash of the brand's own colours, so it is never flatly black. */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(90% 70% at 15% 0%, rgba(99,102,241,.28), transparent 60%),' +
            'radial-gradient(70% 60% at 100% 100%, rgba(168,85,247,.22), transparent 60%)',
        }}
      />
      <div
        className="absolute inset-0 flex gap-16 p-10 opacity-[.24] blur-[1.2px]"
        style={{
          // Gone through the middle, where the window lands, and faint everywhere else.
          // With the blur it is the room the window stands in, not something to read.
          maskImage: 'radial-gradient(78% 68% at 50% 48%, transparent 34%, black 100%)',
          WebkitMaskImage: 'radial-gradient(78% 68% at 50% 48%, transparent 34%, black 100%)',
        }}
      >
        <Column className="-mt-6 flex-1" />
        <Column className="mt-10 hidden flex-1 lg:block" />
      </div>

      {/*
        A calm band down the middle. The mask clears the code around the window, but the
        class's name sits above it and the sign-up line below it, and those have to be
        legible too — wherever the code happens to fall.
      */}
      <div
        className="absolute inset-y-0 left-1/2 w-[48rem] max-w-full -translate-x-1/2"
        style={{
          background:
            'linear-gradient(to right, transparent, rgba(8,10,18,.8) 20%,' +
            'rgba(8,10,18,.8) 80%, transparent)',
        }}
      />
    </div>
  );
}
